import React, { useState } from 'react';
import { Send, Upload, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;
const inputCls = "w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2380]";

const isVisible = (field, answers) => {
  if (!field.show_if) return true;
  const val = answers[field.show_if.field];
  if (Array.isArray(val)) return val.includes(field.show_if.equals);
  return val === field.show_if.equals;
};

export const DynamicForm = ({ formKey, definition, disabled, onSubmitted }) => {
  const [answers, setAnswers] = useState({});
  const [files, setFiles] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState('');

  const setA = (id, v) => setAnswers((p) => ({ ...p, [id]: v }));
  const toggleMulti = (id, opt) => setAnswers((p) => {
    const cur = Array.isArray(p[id]) ? p[id] : [];
    return { ...p, [id]: cur.includes(opt) ? cur.filter((o) => o !== opt) : [...cur, opt] };
  });

  const submit = async (e) => {
    e.preventDefault();
    if (disabled) return;
    setError('');
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('payload', JSON.stringify(answers));
      Object.entries(files).forEach(([id, f]) => { if (f) fd.append(id, f); });
      const res = await fetch(`${API}/api/forms/${formKey}/submit`, { method: 'POST', body: fd });
      const data = await res.json();
      if (res.ok) { setDone(data.message); if (onSubmitted) onSubmitted(); }
      else setError(typeof data.detail === 'string' ? data.detail : 'Controlla i campi obbligatori e riprova.');
    } catch { setError('Errore di connessione. Riprova.'); } finally { setSending(false); }
  };

  if (done) {
    return (
      <div data-testid="dynform-success" ref={(el) => el && el.scrollIntoView({ behavior: 'smooth', block: 'center' })} className="bg-[#0E8C84]/10 border border-[#0E8C84]/30 rounded-2xl p-8 text-center">
        <CheckCircle2 size={40} className="text-[#0E8C84] mx-auto mb-3" />
        <p className="text-lg font-semibold text-gray-800">{done}</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5" data-testid={`dynform-${formKey}`}>
      {(definition.fields || []).map((f) => {
        if (!isVisible(f, answers)) return null;
        const tid = `dynform-${formKey}-${f.id}`;
        const req = f.required ? <span className="text-red-500"> *</span> : null;
        if (f.type === 'section') {
          return <h3 key={f.id} className="text-lg font-bold text-[#1B2380] border-b-2 border-[#1B2380]/20 pb-2 pt-3">{f.label}</h3>;
        }
        if (f.type === 'textarea') {
          return (
            <div key={f.id}>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">{f.label}{req}</label>
              {f.hint && <p className="text-xs text-gray-400 mb-1.5">{f.hint}</p>}
              <textarea data-testid={tid} rows={4} value={answers[f.id] || ''} onChange={(e) => setA(f.id, e.target.value)} placeholder={f.placeholder || ''} className={`${inputCls} resize-y`} />
            </div>
          );
        }
        if (f.type === 'select') {
          return (
            <div key={f.id}>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">{f.label}{req}</label>
              {f.hint && <p className="text-xs text-gray-400 mb-1.5">{f.hint}</p>}
              <select data-testid={tid} value={answers[f.id] || ''} onChange={(e) => setA(f.id, e.target.value)} className={inputCls}>
                <option value="">Seleziona…</option>
                {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          );
        }
        if (f.type === 'radio') {
          return (
            <div key={f.id}>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">{f.label}{req}</label>
              {f.hint && <p className="text-xs text-gray-400 mb-1.5">{f.hint}</p>}
              <div className="flex flex-wrap gap-2">
                {(f.options || []).map((o, i) => (
                  <button key={o} type="button" data-testid={`${tid}-opt${i}`} onClick={() => setA(f.id, o)}
                    className={`rounded-full px-4 py-2 text-sm border-2 transition-colors ${answers[f.id] === o ? 'bg-[#1B2380] border-[#1B2380] text-white' : 'border-gray-200 text-gray-600 hover:border-[#1B2380]/40'}`}>
                    {o}
                  </button>
                ))}
              </div>
            </div>
          );
        }
        if (f.type === 'checkbox') {
          const cur = Array.isArray(answers[f.id]) ? answers[f.id] : [];
          return (
            <div key={f.id}>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">{f.label}{req}</label>
              {f.hint && <p className="text-xs text-gray-400 mb-1.5">{f.hint}</p>}
              <div className="grid sm:grid-cols-2 gap-1.5">
                {(f.options || []).map((o, i) => (
                  <label key={o} className="flex items-start gap-2 text-sm text-gray-600 cursor-pointer">
                    <input data-testid={`${tid}-opt${i}`} type="checkbox" checked={cur.includes(o)} onChange={() => toggleMulti(f.id, o)} className="mt-0.5 w-4 h-4 accent-[#1B2380]" />
                    {o}
                  </label>
                ))}
              </div>
            </div>
          );
        }
        if (f.type === 'file') {
          return (
            <div key={f.id}>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">{f.label}{req}</label>
              <label className={`inline-flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-lg cursor-pointer border-2 ${files[f.id] ? 'border-[#0E8C84] text-[#0B7770]' : 'border-gray-300 text-gray-600 hover:border-[#1B2380]/50'}`}>
                <Upload size={15} /> {files[f.id] ? files[f.id].name : 'Scegli file'}
                <input data-testid={tid} type="file" className="hidden" onChange={(e) => setFiles((p) => ({ ...p, [f.id]: e.target.files[0] }))} />
              </label>
            </div>
          );
        }
        if (f.type === 'consent') {
          return (
            <label key={f.id} className="flex items-start gap-2.5 text-xs text-gray-600 cursor-pointer">
              <input data-testid={tid} type="checkbox" checked={answers[f.id] === true} onChange={(e) => setA(f.id, e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#1B2380]" />
              <span>{f.label}{req} — <Link to="/privacy-policy" className="underline">Privacy</Link> · <Link to="/precontrattuale" className="underline">MUP</Link></span>
            </label>
          );
        }
        return (
          <div key={f.id}>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">{f.label}{req}</label>
            {f.hint && <p className="text-xs text-gray-400 mb-1.5">{f.hint}</p>}
            <input data-testid={tid} type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : f.type === 'email' ? 'email' : f.type === 'tel' ? 'tel' : 'text'}
              value={answers[f.id] || ''} onChange={(e) => setA(f.id, e.target.value)} placeholder={f.placeholder || ''} className={inputCls} />
          </div>
        );
      })}
      {error && <p data-testid="dynform-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
      <button data-testid={`dynform-submit-${formKey}`} type="submit" disabled={sending || disabled}
        className="w-full flex items-center justify-center gap-2 bg-[#1B2380] hover:bg-[#2A3299] text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50">
        <Send size={16} /> {disabled ? 'Invio disattivato in anteprima bozza' : sending ? 'Invio in corso…' : 'Invia la richiesta'}
      </button>
    </form>
  );
};
